import React, { useContext, useEffect, useState } from "react";
import {
    Button,
    Form, FormFeedback,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader
} from "reactstrap";
import Select from "react-select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCountry, getState, updateCustomerShippingAddress } from "../../api/api";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import Loader from "../../components/Loader";

function EditCustomerShippingAddressModal({
                                              isOpen,
                                              toggle,
                                              email,
                                              shippingAddress
                                          }) {
    const { refreshToken } = useContext(AuthContext);
    const queryClient = useQueryClient();

    const [label, setLabel] = useState("");
    const [addressLine1, setAddressLine1] = useState("");
    const [addressLine2, setAddressLine2] = useState("");
    const [addressLine3, setAddressLine3] = useState("");
    const [city, setCity] = useState("");
    const [pincode, setPincode] = useState("");
    const [isPrimaryAddress, setIsPrimaryAddress] = useState(false);

    const [country, setCountry] = useState(null);
    const [stateObj, setStateObj] = useState(null);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);

    // Prefill ONLY when modal opens
    useEffect(() => {
        if (shippingAddress && isOpen) {
            setLabel(shippingAddress.label || "");
            setAddressLine1(shippingAddress.address_line1 || "");
            setAddressLine2(shippingAddress.address_line2 || "");
            setAddressLine3(shippingAddress.address_line3 || "");
            setCity(shippingAddress.city || "");
            setPincode(shippingAddress.pincode || "");
            setIsPrimaryAddress(!!shippingAddress.is_primary);

            setCountry(
                shippingAddress.country
                    ? { value: shippingAddress.country, label: shippingAddress.country }
                    : null
            );

            setStateObj(
                shippingAddress.state
                    ? { value: shippingAddress.state, label: shippingAddress.state }
                    : null
            );
        }
    }, [shippingAddress, isOpen]);

    // Countries
    useQuery(
        ["shipping-country"],
        () => getCountry(1, "", "", "", "", 100),
        {
            enabled: isOpen,
            onSuccess: (data) => {
                setCountryOptions(
                    data.results.map((c) => ({
                        value: c.name,
                        label: c.name
                    }))
                );
            },
            onError: (error) =>
                handleUnauthorized401QueryError(error, refreshToken)
        }
    );

    // States
    useQuery(
        ["shipping-state", country?.value],
        () => getState(1, "", "", country?.value, "", "", 2000),
        {
            enabled: !!country,
            onSuccess: (data) => {
                setStateOptions(
                    data.results.map((s) => ({
                        value: s.name,
                        label: s.name
                    }))
                );
            },
            onError: (error) =>
                handleUnauthorized401QueryError(error, refreshToken)
        }
    );

    const updateCustomerShippingAddressMutation = useMutation({
        mutationFn: updateCustomerShippingAddress,
        onSuccess: () => {
            toast.success("Shipping address updated");
            toggle();
            queryClient.invalidateQueries(["shippingAddress", email]);
        },
        onError: (err, variables) =>
            handleUnauthorized401MutateError(
                err,
                refreshToken,
                updateCustomerShippingAddressMutation,
                variables
            )
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("customer", email);
        formData.append("label", label);
        formData.append("address_line1", addressLine1);
        formData.append("address_line2", addressLine2 || "");
        formData.append("address_line3", addressLine3 || "");
        formData.append("country", country?.value || "");
        formData.append("state", stateObj?.value || "");
        formData.append("city", city);
        formData.append("pincode", pincode);
        formData.append("is_primary", isPrimaryAddress);

        updateCustomerShippingAddressMutation.mutate({
            customerShippingAddressId: shippingAddress.id,
            formData
        });
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} unmountOnClose>
            <ModalHeader toggle={toggle} close={
                <button className="close" onClick={toggle}>
                    &times;
                </button>
            }>
                Edit Shipping Address
            </ModalHeader>

            <ModalBody>
                {shippingAddress && (
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label>Label</Label>
                            <Input
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                invalid={
                                    updateCustomerShippingAddressMutation.isError &&
                                    updateCustomerShippingAddressMutation.error.response.data.label
                                }
                            />
                            {updateCustomerShippingAddressMutation.isError && updateCustomerShippingAddressMutation.error.response.data.label && (
                                <FormFeedback invalid>
                                    {updateCustomerShippingAddressMutation.error.response.data.label}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Address Line 1</Label>
                            <Input
                                value={addressLine1}
                                onChange={(e) => setAddressLine1(e.target.value)}
                                invalid={
                                    updateCustomerShippingAddressMutation.isError &&
                                    updateCustomerShippingAddressMutation.error.response.data.address_line1
                                }
                            />
                            {updateCustomerShippingAddressMutation.isError && updateCustomerShippingAddressMutation.error.response.data.address_line1 && (
                                <FormFeedback invalid>
                                    {updateCustomerShippingAddressMutation.error.response.data.address_line1}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Address Line 2</Label>
                            <Input
                                value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)}
                                invalid={
                                    updateCustomerShippingAddressMutation.isError &&
                                    updateCustomerShippingAddressMutation.error.response.data.address_line2
                                }
                            />
                            {updateCustomerShippingAddressMutation.isError && updateCustomerShippingAddressMutation.error.response.data.address_line2 && (
                                <FormFeedback invalid>
                                    {updateCustomerShippingAddressMutation.error.response.data.address_line2}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Address Line 3</Label>
                            <Input
                                value={addressLine3}
                                onChange={(e) => setAddressLine3(e.target.value)}
                                invalid={
                                    updateCustomerShippingAddressMutation.isError &&
                                    updateCustomerShippingAddressMutation.error.response.data.address_line3
                                }
                            />
                            {updateCustomerShippingAddressMutation.isError && updateCustomerShippingAddressMutation.error.response.data.address_line3 && (
                                <FormFeedback invalid>
                                    {updateCustomerShippingAddressMutation.error.response.data.address_line3}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Country</Label>
                            <Select
                                value={country}
                                options={countryOptions}
                                onChange={(opt) => {
                                    setCountry(opt);
                                    setStateObj(null);
                                    setStateOptions([]);
                                }}
                                styles={{
                                    control: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            borderColor: "#495561",
                                            backgroundColor: "#414d59",
                                            color: "white",
                                            padding: "0.45 0.9",
                                            border: 'none',
                                            boxShadow: 'none'
                                        }
                                    ),
                                    menu: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            backgroundColor: "#414d59",
                                            color: "#a8b5c3"
                                        }
                                    ),
                                    input: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            color: "white"
                                        }
                                    ),
                                    singleValue: (baseStyles) => ({
                                        ...baseStyles,
                                        color: "white"
                                    }),
                                }}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>State</Label>
                            <Select
                                value={stateObj}
                                options={stateOptions}
                                onChange={setStateObj}
                                isDisabled={!country}
                                styles={{
                                    control: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            borderColor: "#495561",
                                            backgroundColor: "#414d59",
                                            color: "white",
                                            padding: "0.45 0.9",
                                            border: 'none',
                                            boxShadow: 'none'
                                        }
                                    ),
                                    menu: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            backgroundColor: "#414d59",
                                            color: "#a8b5c3"
                                        }
                                    ),
                                    input: (baseStyles) => (
                                        {
                                            ...baseStyles,
                                            color: "white"
                                        }
                                    ),
                                    singleValue: (baseStyles) => ({
                                        ...baseStyles,
                                        color: "white"
                                    }),
                                }}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>City</Label>
                            <Input
                                value={city} onChange={(e) => setCity(e.target.value)}
                                invalid={
                                    updateCustomerShippingAddressMutation.isError &&
                                    updateCustomerShippingAddressMutation.error.response.data.city
                                }
                            />
                            {updateCustomerShippingAddressMutation.isError && updateCustomerShippingAddressMutation.error.response.data.city && (
                                <FormFeedback invalid>
                                    {updateCustomerShippingAddressMutation.error.response.data.city}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Pincode</Label>
                            <Input
                                value={pincode} onChange={(e) => setPincode(e.target.value)}
                                invalid={
                                    updateCustomerShippingAddressMutation.isError &&
                                    updateCustomerShippingAddressMutation.error.response.data.pincode
                                }
                            />
                            {updateCustomerShippingAddressMutation.isError && updateCustomerShippingAddressMutation.error.response.data.pincode && (
                                <FormFeedback invalid>
                                    {updateCustomerShippingAddressMutation.error.response.data.pincode}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        <FormGroup check className="mb-3">
                            <Input
                                type="checkbox"
                                checked={isPrimaryAddress}
                                onChange={() => setIsPrimaryAddress(!isPrimaryAddress)}
                                invalid={
                                    updateCustomerShippingAddressMutation.isError &&
                                    updateCustomerShippingAddressMutation.error.response.data.is_primary
                                }
                            />
                            <Label check>Set as Primary Address</Label>
                            {updateCustomerShippingAddressMutation.isError && updateCustomerShippingAddressMutation.error.response.data.is_primary && (
                                <FormFeedback invalid>
                                    {updateCustomerShippingAddressMutation.error.response.data.is_primary}
                                </FormFeedback>
                            )}
                        </FormGroup>

                        {/*<Button*/}
                        {/*    color="primary"*/}
                        {/*    block*/}
                        {/*    disabled={updateCustomerShippingAddressMutation.isLoading}*/}
                        {/*>*/}
                        {/*    {updateCustomerShippingAddressMutation.isLoading*/}
                        {/*        ? <><Loader size="small" /> Updating...</>*/}
                        {/*        : "Update Shipping Address"}*/}
                        {/*</Button>*/}

                        <Button
                            color="#4d5bed"
                            className="bx-btn-primary"
                            block
                            disabled={updateCustomerShippingAddressMutation.isLoading}
                        >
                            {updateCustomerShippingAddressMutation.isLoading? <><Loader size={'small'}/> Updating Shipping Address...</>: `Update Shipping Address`}

                        </Button>
                    </Form>
                )}
            </ModalBody>
        </Modal>
    );
}

export default EditCustomerShippingAddressModal;
